/**
 * DemoPortal Component - HI-SI Landing Page
 * HISIP-016: Embedded read-only execution portal demo
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Play, Loader } from 'lucide-react';
import { ExecutionPortalData, StageData } from '@/types/portal';
import { StageType } from '@/types/execution';
import AtmosphericBackground from '@/components/futuristic/AtmosphericBackground';
import HolographicCard from '@/components/futuristic/HolographicCard';
import { cn } from '@/utils/cn';

// Stage configuration matching ExecutionPortal
const STAGE_CONFIG = {
  script: { name: 'Script', color: 'purple', glow: 'rgba(168, 85, 247' },
  vision: { name: 'Vision', color: 'cyan', glow: 'rgba(6, 182, 212' },
  storyboard: { name: 'Storyboard', color: 'emerald', glow: 'rgba(16, 185, 129' },
  shots: { name: 'Shots', color: 'amber', glow: 'rgba(245, 158, 11' },
  continuity: { name: 'Continuity', color: 'red', glow: 'rgba(239, 68, 68' },
  frames: { name: 'Frames', color: 'indigo', glow: 'rgba(99, 102, 241' },
  videos: { name: 'Videos', color: 'teal', glow: 'rgba(20, 184, 166' },
  elements: { name: 'Elements', color: 'pink', glow: 'rgba(236, 72, 153' },
  qc: { name: 'QC', color: 'green', glow: 'rgba(34, 197, 94' },
  assemble: { name: 'Assemble', color: 'yellow', glow: 'rgba(234, 179, 8' },
  music: { name: 'Music', color: 'cyan', glow: 'rgba(6, 182, 212' },
  publish: { name: 'Publish', color: 'violet', glow: 'rgba(139, 92, 246' },
  music_master: { name: 'Music Master', color: 'slate', glow: 'rgba(100, 116, 139' },
  music_sidechain: { name: 'Music Sidechain', color: 'stone', glow: 'rgba(120, 113, 108' },
  export_preview: { name: 'Export Preview', color: 'orange', glow: 'rgba(249, 115, 22' }
} as const;

const STAGES: StageType[] = ['script', 'vision', 'storyboard', 'shots', 'continuity', 'frames', 'videos'];

// Create fallback demo snapshot (declared above component so it can be used for initial state)
function createFallbackDemo(): ExecutionPortalData {
  return {
    id: 'demo-fallback',
    status: 'completed' as const,
    current_stage: 'videos' as StageType,
    progress: { overall: 100, current_stage: 7, stages_completed: 7, total_stages: 7 },
    elapsed_time: 2700,
    estimated_remaining: 0,
    cost_incurred: 12.45,
    cost_projected: 12.45,
    actual_cost_usd: 12.45,
    avg_quality_score: 87,
    frames_passed_target: 85,
    frames_passed_fallback: 15,
    strategy_adaptations: 3,
    created_at: '2024-01-15T10:00:00Z',
    updated_at: '2024-01-15T10:45:00Z',
    stages: Object.fromEntries(
      STAGES.map(stage => [
        stage,
        {
          status: 'completed' as const,
          iterations: Math.floor(Math.random() * 3) + 2,
          quality: 75 + Math.floor(Math.random() * 20),
          persona: stage === 'script' || stage === 'vision' || stage === 'storyboard' 
            ? 'The Architect' : 'The Operator'
        }
      ])
    ) as Record<StageType, StageData>
  };
}

interface DemoPortalProps {
  className?: string;
}

export default function DemoPortal({ className }: DemoPortalProps) {
  // Initialize with a fallback so the demo always renders something even if network fails
  const [demoExecution, setDemoExecution] = useState<ExecutionPortalData | null>(createFallbackDemo());
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStage, setCurrentStage] = useState<StageType>('script');
  const [selectedStage, setSelectedStage] = useState<StageType | null>(null);

  // Load demo execution data
  useEffect(() => {
    const loadDemoData = async () => {
      try {
        const url = '/demo/demo-execution.json';
        const response = await fetch(url, {
          cache: 'no-store',
          credentials: 'include'
        });
        if (!response.ok) {
          throw new Error(`Failed to load demo data (${response.status})`);
        }
        const data = await response.json();
        
        // Transform data to match ExecutionPortalData structure
        const transformedData: ExecutionPortalData = {
          ...data,
          stages: data.stages as Record<StageType, StageData>
        };
        
        setDemoExecution(transformedData);
      } catch (error) {
        console.error('Failed to load demo execution; using fallback:', error);
        // Keep the existing fallback demoExecution
      } finally {
        setIsLoading(false);
      }
    };

    loadDemoData();
  }, []);

  // Simulate pipeline execution
  const playDemo = () => {
    if (isPlaying || !demoExecution) return;
    
    setIsPlaying(true);
    setCurrentStage('script');
    
    // Reset all stages to pending
    const resetExecution = {
      ...demoExecution,
      current_stage: 'script' as StageType,
      stages: Object.fromEntries(
        STAGES.map(stage => [
          stage,
          { ...demoExecution.stages[stage], status: 'pending' as const }
        ])
      ) as Record<StageType, StageData>
    };
    
    setDemoExecution(resetExecution);
    
    // Animate through stages
    let stageIndex = 0;
    const interval = setInterval(() => {
      if (stageIndex >= STAGES.length) {
        clearInterval(interval);
        setIsPlaying(false);
        // Restore completed state
        setDemoExecution(prev => prev ? {
          ...prev,
          current_stage: 'videos' as StageType,
          stages: Object.fromEntries(
            STAGES.map(stage => [
              stage,
              { ...prev.stages[stage], status: 'completed' as const }
            ])
          ) as Record<StageType, StageData>
        } : null);
        return;
      }
      
      const stage = STAGES[stageIndex];
      setCurrentStage(stage);
      
      // Update stage status to running
      setDemoExecution(prev => prev ? {
        ...prev,
        current_stage: stage,
        stages: {
          ...prev.stages,
          [stage]: { ...prev.stages[stage], status: 'running' as const }
        }
      } : null);
      
      // After 1.5 seconds, mark as completed and move to next
      setTimeout(() => {
        setDemoExecution(prev => prev ? {
          ...prev,
          stages: {
            ...prev.stages,
            [stage]: { ...prev.stages[stage], status: 'completed' as const }
          }
        } : null);
      }, 1500);
      
      stageIndex++;
    }, 2000);
  };

  if (isLoading) {
    return (
      <div className={cn("relative min-h-[600px] rounded-2xl overflow-hidden", className)}>
        <div className="absolute inset-0 bg-black/50 backdrop-blur-xl border border-white/10">
          <AtmosphericBackground />
          <div className="relative z-10 flex items-center justify-center h-full">
            <div className="text-center">
              <Loader className="w-8 h-8 text-cyan-400 animate-spin mx-auto mb-4" />
              <p className="text-white/70 font-mono text-sm">Loading Demo Portal...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!demoExecution) {
    return (
      <div className={cn("relative min-h-[600px] rounded-2xl overflow-hidden", className)}>
        <div className="absolute inset-0 bg-black/50 backdrop-blur-xl border border-red-500/20">
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-red-400 font-mono text-sm">Demo unavailable</p>
              <p className="text-white/50 text-xs mt-2">Using fallback visualization</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const completedStages = STAGES.filter(s => demoExecution?.stages?.[s]?.status === 'completed').length;
  const overallProgress = (completedStages / STAGES.length) * 100;

  return (
    <div className={cn("relative min-h-[600px] rounded-2xl overflow-hidden", className)}>
      {/* Demo Portal Background */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-xl border border-cyan-500/20">
        <AtmosphericBackground />
        
        {/* Demo Badge */}
        <div className="absolute top-4 left-4 z-20">
          <div className="px-3 py-1 rounded-full bg-cyan-500/20 border border-cyan-500/30">
            <span className="text-xs font-mono text-cyan-400 uppercase tracking-wide">
              Live Demo
            </span>
          </div>
        </div>
        
        {/* Play Demo Button */}
        {!isPlaying && (
          <div className="absolute top-4 right-4 z-20">
            <motion.button
              onClick={playDemo}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-500/20 border border-purple-500/30 hover:bg-purple-500/30 transition-colors"
            >
              <Play className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-mono text-purple-400">Run Pipeline</span>
            </motion.button>
          </div>
        )}
        
        {/* Demo Content */}
        <div className="relative z-10 p-6 h-full flex flex-col">
          {/* Header */}
          <div className="mb-6">
            <h3 className="text-xl font-light text-white mb-2">
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Creative Portal
              </span>
            </h3>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-white/60">Progress:</span>
                <span className="text-cyan-400 font-mono">{overallProgress.toFixed(0)}%</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-white/60">Quality:</span>
                <span className="text-emerald-400 font-mono">{demoExecution?.avg_quality_score || 0}%</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-white/60">Cost:</span>
                <span className="text-green-400 font-mono">${(demoExecution?.cost_incurred || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>
          
          {/* Pipeline Visualization - Simplified for demo */}
          <div className="flex-1 flex items-center justify-center">
            <div className="relative w-full max-w-2xl">
              
              {/* Central Brain */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                <motion.div
                  animate={{ 
                    scale: isPlaying ? [1, 1.1, 1] : 1,
                    filter: isPlaying 
                      ? ['drop-shadow(0 0 20px rgba(168, 85, 247, 0.4))', 'drop-shadow(0 0 40px rgba(168, 85, 247, 0.8))', 'drop-shadow(0 0 20px rgba(168, 85, 247, 0.4))']
                      : 'drop-shadow(0 0 20px rgba(168, 85, 247, 0.4))'
                  }}
                  transition={{ duration: 2, repeat: isPlaying ? Infinity : 0, ease: "easeInOut" }}
                  className="w-16 h-16 rounded-full bg-black/50 backdrop-blur-xl border border-purple-500/30 flex items-center justify-center"
                >
                  <Brain className="w-8 h-8 text-purple-400" />
                </motion.div>
                
                {/* Current stage indicator */}
                {isPlaying && currentStage && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-center"
                  >
                    <div className="text-xs text-purple-300 whitespace-nowrap flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-pulse" />
                      Processing {STAGE_CONFIG[currentStage].name}
                    </div>
                  </motion.div>
                )}
              </div>
              
              {/* Stage Nodes in Circle */}
              <div className="relative h-80 w-80 mx-auto">
                {demoExecution && STAGES.map((stage, index) => {
                  const angle = (index * 2 * Math.PI) / STAGES.length - Math.PI / 2;
                  const radius = 140;
                  const x = Math.cos(angle) * radius;
                  const y = Math.sin(angle) * radius;
                  const stageData = demoExecution.stages?.[stage] || { status: 'pending', iterations: 0, quality: 0, persona: 'Unknown' };
                  const config = STAGE_CONFIG[stage];
                  
                  return (
                    <motion.div
                      key={stage}
                      className="absolute"
                      style={{
                        left: `50%`,
                        top: `50%`,
                        transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`
                      }}
                      animate={{
                        scale: stageData.status === 'running' ? [1, 1.1, 1] : 1
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: stageData.status === 'running' ? Infinity : 0,
                        ease: "easeInOut"
                      }}
                    >
                      <motion.div
                        className={cn(
                          "w-12 h-12 rounded-full cursor-pointer",
                          "bg-black/50 backdrop-blur-xl border-2 transition-all duration-300",
                          "flex flex-col items-center justify-center",
                          stageData.status === 'running' && "border-cyan-400 animate-pulse",
                          stageData.status === 'completed' && "border-emerald-400",
                          stageData.status === 'pending' && "border-white/20"
                        )}
                        onClick={() => setSelectedStage(stage)}
                        whileHover={{ scale: 1.1 }}
                        style={{
                          boxShadow: stageData.status === 'completed' ? `0 0 20px ${config.glow}, 0.4)` :
                                   stageData.status === 'running' ? `0 0 20px rgba(6, 182, 212, 0.6)` : 'none'
                        }}
                      >
                        <div className="text-[10px] font-mono text-white/80 text-center">
                          {config.name}
                        </div>
                        {stageData.quality > 0 && (
                          <div className="text-[8px] font-mono text-emerald-400">
                            {Math.round(stageData.quality)}%
                          </div>
                        )}
                        {stageData.iterations > 0 && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-cyan-500 flex items-center justify-center">
                            <span className="text-[8px] text-white font-bold">{stageData.iterations}</span>
                          </div>
                        )}
                      </motion.div>
                    </motion.div>
                  );
                }) || []}
              </div>
            </div>
          </div>
          
          {/* Stage Details Panel */}
          <AnimatePresence>
            {selectedStage && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="mt-4"
              >
                <HolographicCard glow="cyan" intensity="subtle" className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-cyan-400 font-mono text-sm uppercase">
                      {STAGE_CONFIG[selectedStage].name} Stage
                    </h4>
                    <button
                      onClick={() => setSelectedStage(null)}
                      className="text-white/50 hover:text-white/80 text-sm"
                    >
                      ×
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-xs">
                    <div>
                      <span className="text-white/60">Status:</span>
                      <div className={cn(
                        "font-mono uppercase mt-1",
                        (demoExecution.stages[selectedStage]?.status || 'pending') === 'completed' ? 'text-emerald-400' :
                        (demoExecution.stages[selectedStage]?.status || 'pending') === 'running' ? 'text-cyan-400' : 'text-white/60'
                      )}>
                        {demoExecution.stages[selectedStage]?.status || 'pending'}
                      </div>
                    </div>
                    <div>
                      <span className="text-white/60">Quality:</span>
                      <div className="text-emerald-400 font-mono mt-1">
                        {Math.round(demoExecution.stages[selectedStage]?.quality || 0)}%
                      </div>
                    </div>
                    <div>
                      <span className="text-white/60">Iterations:</span>
                      <div className="text-cyan-400 font-mono mt-1">
                        {demoExecution.stages[selectedStage]?.iterations || 0}
                      </div>
                    </div>
                  </div>
                </HolographicCard>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}